import { useRef, useCallback } from "react"
import { streamAdkMessage, generateSessionId } from "utils/adkChat"
import { sendMessage, setBotTyping, Group } from "firebase/groupsService"

const BOT_ID = "bot"
const BOT_NAME = "agentD"
const SESSION_RESET_AFTER = 20

export function useBotReply(loggedUserId: string) {
	const sessionsRef = useRef<Map<string, string>>(new Map())
	const turnCountRef = useRef<Map<string, number>>(new Map())
	const abortRef = useRef<AbortController | null>(null)

	const getSession = (groupId: string): string => {
		const turns = turnCountRef.current.get(groupId) ?? 0
		if (!sessionsRef.current.has(groupId) || turns >= SESSION_RESET_AFTER) {
			sessionsRef.current.set(groupId, generateSessionId())
			turnCountRef.current.set(groupId, 0)
		}
		return sessionsRef.current.get(groupId)!
	}

	const triggerBotReply = useCallback(
		async (group: Group, userMessage: string) => {
			if (!group.hasBotActive) return
			if (group.botTyping) return

			abortRef.current?.abort()
			const abort = new AbortController()
			abortRef.current = abort

			const sessionId = getSession(group.id)

			try {
				await setBotTyping(group.id, true)

				let finalText = ""
				const stream = streamAdkMessage({
					userId: loggedUserId,
					sessionId,
					message: userMessage,
					signal: abort.signal,
				})

				for await (const text of stream) {
					if (abort.signal.aborted) break
					finalText = text
				}

				if (finalText && !abort.signal.aborted) {
					await sendMessage({
						groupId: group.id,
						content: finalText,
						senderId: BOT_ID,
						senderType: "bot",
						senderName: BOT_NAME,
					})
					turnCountRef.current.set(
						group.id,
						(turnCountRef.current.get(group.id) ?? 0) + 1
					)
				}
			} catch (err) {
				const e = err as Error
				if (e?.name !== "AbortError") {
					console.error("[useBotReply]", e)
				}
			} finally {
				setBotTyping(group.id, false).catch(() => {})
			}
		},
		[loggedUserId]
	)

	return { triggerBotReply }
}
